import { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { DeviseApi } from '../api/deviseApi'
import { Auth } from '../utils/Auth'

const DeviseContext = createContext(null)

const STORAGE_KEY = 'selected_devise_id'

export const DeviseProvider = ({ children }) => {
    const [devises, setDevises] = useState([])
    const [selectedDevise, setSelectedDevise] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
        const abortController = new AbortController()

        DeviseApi.getActiveDevises(abortController.signal)
            .then((res) => {
                if (!res || !res.success) {
                    setLoading(false)
                    return
                }

                const list = res.data || []
                setDevises(list)

                const savedId = localStorage.getItem(STORAGE_KEY)
                const fromSaved = savedId && list.find((d) => String(d.id) === savedId)
                const fromDefault = list.find((d) => d.is_default)

                setSelectedDevise(fromSaved || fromDefault || list[0] || null)
                setLoading(false)
            })
            .catch(() => setLoading(false))

        return () => abortController?.abort()
    }, [])

    const selectDevise = useCallback((devise) => {
        setSelectedDevise(devise)
        localStorage.setItem(STORAGE_KEY, String(devise.id))

        if (Auth.getSessionToken()) {
            DeviseApi.updatePreference(devise.id).catch(() => {})
        }
    }, [])

    // Convertit un montant exprimé dans la devise de référence (XOF) vers la devise sélectionnée
    const convert = useCallback((amountInReference) => {
        if (amountInReference === null || amountInReference === undefined || amountInReference === '') {
            return null
        }

        const amount = Number(amountInReference)
        if (Number.isNaN(amount)) return null

        if (!selectedDevise) return amount

        const decimals = selectedDevise.decimal_places ?? 2

        if (selectedDevise.is_default) {
            return Number(amount.toFixed(decimals))
        }

        const rate = Number(selectedDevise.exchange_rate) || 0
        return Number((amount * rate).toFixed(decimals))
    }, [selectedDevise])

    // Convertit et formate (symbole + position) un montant exprimé en devise de référence (XOF)
    const format = useCallback((amountInReference) => {
        const converted = convert(amountInReference)

        if (converted === null) return '-'
        if (!selectedDevise) return converted

        const decimals = selectedDevise.decimal_places ?? 2
        const formatted = new Intl.NumberFormat('fr-FR', {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        }).format(converted)

        return selectedDevise.symbol_position === 'before'
            ? `${selectedDevise.symbol} ${formatted}`
            : `${formatted} ${selectedDevise.symbol}`
    }, [convert, selectedDevise])

    return (
        <DeviseContext.Provider value={{ devises, selectedDevise, selectDevise, convert, format, loading }}>
            {children}
        </DeviseContext.Provider>
    )
}

export const useDevise = () => {
    const ctx = useContext(DeviseContext)
    if (!ctx) {
        throw new Error('useDevise must be used within a DeviseProvider')
    }
    return ctx
}
